import { SourceMapConsumer, type RawSourceMap } from 'source-map-js';
import type { CodeFrameString, SentryStackFrame } from '../types';

let warned = false;

/**
 * Returns a nicely-formatted string with line numbers and a “>” pointer.
 * Example:
 *
 *   41 │   const data = await fetchUser();
 * > 42 │   console.log(undefinedVariable);
 *   43 │   return <div>{data.name}</div>;
 */
export async function getCodeFrame(
  frame: SentryStackFrame,
  ctxLines = 5,
): Promise<CodeFrameString | null> {
  if (!frame.file || frame.lineNumber == null || frame.column == null) return null;

  try {
    // -- load & consume the source-map --------------------------------------------------
    const mapResp = await fetch(`${frame.file}.map`);
    if (!mapResp.ok) throw new Error('map 404');
    const raw = (await mapResp.json()) as RawSourceMap;
    const consumer = await new SourceMapConsumer(raw);

    const orig = consumer.originalPositionFor({
      line: frame.lineNumber,
      column: frame.column,
      bias: SourceMapConsumer.GREATEST_LOWER_BOUND,
    });

    const content =
      (orig.source && consumer.sourceContentFor(orig.source, true)) || null;
    // @ts-ignore
    consumer.destroy?.();

    if (!content || orig.line == null) return null;

    // -- build the pretty diff-style string --------------------------------------------
    const lines = content.split('\n');
    const start = Math.max(0, orig.line - 1 - ctxLines);
    const end = Math.min(lines.length, orig.line + ctxLines);
    const width = String(end).length; // pad line numbers

    const snippet = lines
      .slice(start, end)
      .map((ln, idx) => {
        const realLine = start + idx + 1;
        const prefix = realLine === orig.line ? '>' : ' ';
        return `${prefix} ${String(realLine).padStart(width)} │ ${ln}`;
      })
      .join('\n');

    return {
      file: orig.source!,
      line: orig.line,
      column: orig.column ?? 0,
      snippet,          // ← string instead of array
    };
  } catch (e) {
    if (!warned) {
      console.warn('[creatr-devtools] snippet resolver failed', e);
      warned = true;
    }
    return null;
  }
}
